/**
 * The 8 screen-compass facings, game side.
 *
 * Mirrors `tools/sims-pipeline/src/render/facing.py`, which is where the mapping
 * is derived and self-checked. Both files must agree: the renderer decides which
 * world yaw is called `se`, and this file decides which movement is called `se`.
 * If they drift, the character walks one way and faces another.
 */

export const DIRECTIONS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;
export type Direction = (typeof DIRECTIONS)[number];

/** One step in each direction, in sub-tile coordinates. */
export const DIRECTION_DELTA: Record<Direction, [number, number]> = {
  n: [-1, -1],
  ne: [0, -1],
  e: [1, -1],
  se: [1, 0],
  s: [1, 1],
  sw: [0, 1],
  w: [-1, 1],
  nw: [-1, 0],
};

/** The facing whose direction is closest to the given tile-space movement. */
export function directionForDelta(dTileX: number, dTileY: number): Direction {
  const len = Math.hypot(dTileX, dTileY) || 1;
  const ux = dTileX / len;
  const uy = dTileY / len;

  let best: Direction = 's';
  let bestDot = -Infinity;
  for (const dir of DIRECTIONS) {
    const [dx, dy] = DIRECTION_DELTA[dir];
    const m = Math.hypot(dx, dy) || 1;
    const dot = ux * (dx / m) + uy * (dy / m);
    if (dot > bestDot) {
      bestDot = dot;
      best = dir;
    }
  }
  return best;
}
